import { rankOf, starString } from "./composter";
import { type CompostEffectId } from "./compostEffects";

/**
 * A COMPOST as a pot carries it. Composts are made by the scrap composter (sim/composter.ts): every harvest
 * feeds it scraps, and each finished batch is ranked 1–5★ with 1–3 effects. A compost placed in a pot stays
 * there for every plant grown in it until it is swapped for another, and a growing plant adopts the pot's
 * compost when it is sown (`rec.compost`). The effects are paid at harvest (useGreenhouse `harvestPot`) and,
 * for the two passives, read continuously: Growth Speed (`quickBloomSpeed`) and Moisture Lock (`moistureLock`).
 *
 * `rank` is absent on batches made by the old (elemental) composter; `rankOf` derives one from their
 * strongest effect, so they display on the same 1–5★ scale.
 */

/** An effect ON a compost: its rolled `amount` (the stat, whole percent) + the [min,max] band it rolled in. */
export interface CompostEffect { type: CompostEffectId; amount: number; min: number; max: number; }
export interface CompostBatch { effects: CompostEffect[]; rank?: number; }

/** The grow-speed multiplier contribution (≥0, additive) of a compost's GROWTH SPEED effect; 0 if absent. */
export function quickBloomSpeed(b?: CompostBatch): number {
  if (!b) return 0;
  const qb = b.effects.find((e) => e.type === "quickBloom");
  return qb ? qb.amount / 100 : 0;
}

/** The MOISTURE-LOCK strength of a compost (0..0.8): how strongly it slows soil moisture change (moisture.ts). */
export function moistureLock(b?: CompostBatch): number {
  if (!b) return 0;
  const r = b.effects.find((e) => e.type === "retain");
  return r ? Math.max(0, Math.min(0.8, r.amount / 100)) : 0;
}

/** A compost's display name: its rank, e.g. "★★★ Compost". */
export function batchName(b: CompostBatch): string {
  return `${starString(rankOf(b))} Compost`;
}
export { effectDef } from "./compostEffects";
